import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, MapPin, Rocket, CreditCard, AlertTriangle, Clock, Package, Globe } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SimpleMap from '../components/SimpleMap';

gsap.registerPlugin(ScrollTrigger);

const Delivery: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    // Hero анимация
    if (titleRef.current) {
      gsap.fromTo(titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out"
        }
      );
    }

    if (subtitleRef.current) {
      gsap.fromTo(subtitleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          delay: 0.3
        }
      );
    }

    // Анимации при скролле
    if (containerRef.current) {
      gsap.fromTo(containerRef.current.children,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 80%',
            end: 'bottom 5%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }
  }, []);




  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-4 transition-all duration-300 hover:scale-105 group"
          >
            <ArrowLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" />
            <span>Вернуться на главную</span>
          </Link>
          <h1 ref={titleRef} className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Доставка и оплата
          </h1>
          <p ref={subtitleRef} className="text-xl text-gray-600 dark:text-gray-300 mt-4 max-w-3xl">
            Удобные способы доставки и безопасные методы оплаты для вашего комфорта
          </p>
        </div>

        {/* Content */}
        <div ref={containerRef} className="space-y-8">
          {/* Блок 1: Курьерская служба */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <Truck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Курьерская служба
              </h2>
            </div>
            
            <div className="space-y-3 text-gray-600 dark:text-gray-300">
              <div className="flex items-center space-x-3">
                <Package className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Доставим прямо до двери в удобное время</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Курьерская доставка по городу</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Возможность выбрать удобный день и время получения</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F86049' }}>
                  <span className="text-white text-xs font-bold">Я</span>
                </div>
                <span>Оповещение о прибытии курьера</span>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <strong>Доставка будет через Яндекс Доставку</strong>
              </p>
            </div>
          </div>

          {/* Самовывоз */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg delivery-block">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-3">
                <MapPin className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                <span>Самовывоз</span>
              </h2>
              
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300">
                  Выберите удобный пункт самовывоза на карте. Кликните на маркер для получения подробной информации.
                </p>
                
                <div className="bg-gray-200 dark:bg-gray-700 rounded-xl h-96 overflow-hidden">
                  <SimpleMap />
                </div>
              </div>

              {/* Блок с преимуществами, списком ПВЗ и временем работы временно скрыт */}
            </div>
          </div>

          {/* Блок 3: Доставка по России */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <Rocket className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Доставка по России
              </h2>
            </div>
            
            <div className="space-y-3 text-gray-600 dark:text-gray-300">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-primary-500 rounded-full flex-shrink-0"></div>
                <span>Быстрая и надёжная доставка в любой регион</span>
              </div>
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Через партнёрские службы доставки</span>
              </div>
              <div className="flex items-center space-x-3">
                <Truck className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Возможен курьер или самовывоз из пунктов выдачи</span>
              </div>
              <div className="flex items-center space-x-3">
                <Package className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span>Онлайн-отслеживание посылки</span>
              </div>
            </div>
          </div>

          {/* Блок 4: Оплата */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Оплата
              </h2>
            </div>
            
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              Вы выбираете удобный способ оплаты — мы принимаем <strong className="text-primary-600 dark:text-primary-400">практически всё</strong>:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-primary-500 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600 dark:text-gray-300">Банковские карты</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-primary-500 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600 dark:text-gray-300">Онлайн-кошельки</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-primary-500 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600 dark:text-gray-300">Системы быстрых платежей</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-primary-500 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-600 dark:text-gray-300">QR-код в приложении банка</span>
                </div>
              </div>
            </div>
          </div>

          {/* Блок 5: О возврате */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
                О возврате
              </h2>
            </div>
            
            <p className="text-lg text-yellow-700 dark:text-yellow-300 mb-4">
              Уважаемые покупатели, обратите внимание:
            </p>
            
            <div className="space-y-3 text-yellow-700 dark:text-yellow-300">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></div>
                <span>Мы не предоставляем услуги частичного выкупа заказа</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></div>
                <span>Нет частичного возврата заказа</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></div>
                <span>Нет предварительной проверки заказа до оплаты — ни у курьера, ни в пункте самовывоза</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Delivery;
